import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

import api from "../../services/api";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { relativeTime, exactTime } from "../../utils/time";
import { Confirm, FormError, ModalFoot, PanelBody } from "../../components/ui/panelKit";

const LABELS = { origin: "Manba" };

/**
 * Local check for the three mistakes the gateway cannot forgive.
 *
 * The server validates all of this too, but its answers are in English and
 * arrive after the modal has been filled in and submitted. The value ends up
 * verbatim in the widget page's `frame-ancestors` and is compared to the
 * socket's Origin header by exact string match, so a trailing slash or a path
 * produces an entry that can never match anything: the customer sees their
 * origin listed and still cannot frame the widget.
 */
const validateOrigin = (raw) => {
  const value = raw.trim();
  if (!value) return "Manbani kiriting, masalan https://example.uz";
  if (!/^https?:\/\//.test(value))
    return "Manba http:// yoki https:// bilan boshlanishi kerak.";
  const host = value.split("://")[1];
  if (!host) return "Xost koʻrsatilmagan. Masalan: https://example.uz";
  if (host.includes("/"))
    return "Manbada yoʻl (path) boʻlmaydi. https://example.uz deb yozing, https://example.uz/chat emas.";
  if (value.startsWith("http://") && !/^(localhost|127\.0\.0\.1)/.test(host))
    return "Shifrlanmagan http:// faqat localhost uchun. Qolganlari https:// boʻlishi kerak.";
  return "";
};

/**
 * Which sites may embed this client's widget.
 *
 * The list is the first place to look when a customer reports that the chat
 * does not appear: a missing origin means the browser refuses to render the
 * widget and says nothing about why.
 */
const OriginsPanel = ({ clientId, onCount }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [origin, setOrigin] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/clients/${clientId}/origins/`);
      const list = res.data || [];
      setRows(list);
      onCount?.(list.length);
    } catch (err) {
      setError(readApiError(err, LABELS, "Manbalar roʻyxatini yuklab boʻlmadi."));
    } finally {
      setLoading(false);
    }
  }, [clientId, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const complaint = validateOrigin(origin);
    if (complaint) {
      setFormError(complaint);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.post(`/clients/${clientId}/origins/`, { origin: origin.trim() });
      notify.success("Manba qoʻshildi");
      setOpen(false);
      setOrigin("");
      await load();
    } catch (err) {
      setFormError(readApiError(err, LABELS, "Manbani qoʻshib boʻlmadi."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      // The origin is the last path segment, so it must be escaped -- it
      // contains `://`. The trailing slash is not optional either: the router
      // is a DefaultRouter, and without it the route does not resolve and the
      // request comes back 404 as if the origin were not there.
      await api.delete(
        `/clients/${clientId}/origins/${encodeURIComponent(confirm.origin)}/`,
      );
      notify.success("Manba oʻchirildi");
      setConfirm(null);
      await load();
    } catch (err) {
      notify.error(readApiError(err, LABELS, "Manbani oʻchirib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ms-panel">
      <div className="ms-panel-head">
        <h2>Ruxsat etilgan manbalar</h2>
        <div className="ms-toolbar-spacer" />
        {!loading && rows.length === 0 && (
          <span className="ms-badge ms-badge--progress">manba yoʻq</span>
        )}
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--sm"
          onClick={() => {
            setOrigin("");
            setFormError("");
            setOpen(true);
          }}
        >
          <Icon icon="tabler:plus" />
          Manba
        </button>
      </div>

      <PanelBody
        loading={loading}
        error={error}
        empty={!rows.length}
        emptyText="Manba yoʻq — vidjet hech qayerda ochilmaydi"
      >
        <div className="ms-table-wrap">
          <table className="ms-table ms-table--compact">
            <thead>
              <tr>
                <th>Manba</th>
                <th style={{ width: 140 }}>Qoʻshilgan</th>
                <th className="ms-td-actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.origin}>
                  <td>
                    <span className="ms-mono">{o.origin}</span>
                  </td>
                  <td className="ms-td-muted" title={exactTime(o.created_at)}>
                    {relativeTime(o.created_at)}
                  </td>
                  <td className="ms-td-actions">
                    <div className="ms-rowacts">
                      <button
                        type="button"
                        className="ms-rowact is-danger"
                        title="Oʻchirish"
                        aria-label={`${o.origin} manbasini oʻchirish`}
                        onClick={() => setConfirm(o)}
                      >
                        <Icon icon="tabler:trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelBody>

      <Modal
        open={open}
        title="Manba qoʻshish"
        size="sm"
        onClose={() => setOpen(false)}
        footer={
          <ModalFoot
            onCancel={() => setOpen(false)}
            onSave={add}
            saving={saving}
            saveLabel="Qoʻshish"
            savingLabel="Qoʻshilmoqda..."
          />
        }
      >
        <div className="ms-form-grid ms-form-grid--stack">
          <div className="ms-field ms-field--full">
            <label htmlFor="ms-origin">
              Manba <span>*</span>
            </label>
            <input
              id="ms-origin"
              type="text"
              placeholder="https://example.uz"
              value={origin}
              autoFocus
              onChange={(e) => setOrigin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <span className="ms-field-hint">
              Faqat sxema, xost va port: https://example.uz yoki
              https://example.uz:8443. Yoʻl (/chat) yozilmaydi.
            </span>
          </div>
          <FormError>{formError}</FormError>
        </div>
      </Modal>

      <Confirm
        open={!!confirm}
        busy={busy}
        name={confirm?.origin}
        title="Manbani oʻchirish"
        message="Bu saytda vidjet darhol ishlamay qoladi — brauzer uni koʻrsatishdan bosh tortadi. Kerak boʻlsa, manbani qaytadan qoʻshish mumkin."
        onConfirm={remove}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default OriginsPanel;
